// Shared message card — one affordance, two consumers. Main chat
// (ProjectRoomPage) wraps a rich body with attachments + cost badge;
// drawer chat (SimpleChat) wraps a simple markdown body. Both render
// the same card chrome: bg-surface-raised rounded card, author label
// at the top (when not same-author), optional timestamp + delete
// button, indent gap on the opposite side so the author's "side"
// reads at a glance.
//
// Extracted 2026-04-24 per Captain + Reef audit — Composer and
// drawer had drifted. MessageCard closes the loop on the message
// row the same way ChatInput did for the composer.

import type { ReactNode } from "react";
import { ArmedDeleteButton } from "./ArmedDeleteButton";

export interface MessageCardAuthor {
  /** Display name — rendered at the top-left of the card. */
  name: string;
  /** Tailwind text-color class (e.g. `text-accent`, `text-primary`,
   * `text-warning`). */
  colorClass: string;
  /** Optional monospace tag (e.g. `@seat_key`) shown beside the name. */
  meta?: string;
}

export function MessageCard({
  author,
  alignment,
  sameAuthor = false,
  timestamp,
  onDelete,
  deleteArmed = false,
  testId,
  children,
}: {
  author: MessageCardAuthor;
  /** "me" → indent left margin so user messages shift right; "other"
   * → indent right margin so agent / system messages shift left. The
   * card stays the full interior width minus the indent so the body
   * isn't cramped. */
  alignment: "me" | "other";
  /** When the prior message has the same author, collapse spacing to
   * mt-0.5 and hide the name header (timestamp still shows on hover). */
  sameAuthor?: boolean;
  timestamp?: string;
  onDelete?: () => void;
  deleteArmed?: boolean;
  testId?: string;
  children: ReactNode;
}) {
  // "me" sits with space on the right (indented from the right
  // edge); "other" sits with space on the left (indented from the
  // left edge). Captain 2026-04-24: reversed from the initial
  // convention — user's own messages should read as "outgoing" on
  // the right.
  const indent = alignment === "me" ? "mr-6" : "ml-6";
  const spacing = sameAuthor ? "mt-0.5" : "mt-2 first:mt-0";
  return (
    <li
      className={`group relative rounded-md bg-surface-raised px-3 py-2 transition-colors hover:bg-surface-raised/80 ${spacing} ${indent}`}
      data-testid={testId}
    >
      {onDelete && (
        <ArmedDeleteButton
          armed={deleteArmed}
          onDelete={onDelete}
          size={12}
          label="Delete message"
          testId={testId ? `${testId}-delete` : undefined}
          className="absolute right-1.5 top-1.5"
        />
      )}
      {!sameAuthor && (
        <div className="mb-0.5 flex items-baseline gap-2 pr-2">
          <span className={`text-sm font-semibold ${author.colorClass}`}>
            {author.name}
          </span>
          {author.meta && (
            <span className="font-code text-xs text-faint">{author.meta}</span>
          )}
          {timestamp && (
            <span className="text-xs text-faint">{timestamp}</span>
          )}
        </div>
      )}
      {sameAuthor && timestamp && (
        <span className="absolute right-3 top-2 hidden text-xs text-faint group-hover:inline-block">
          {timestamp}
        </span>
      )}
      {children}
    </li>
  );
}
